// Remembered-device storage for the family portal.
//
// The magic_token itself is the credential; we simply persist it locally with
// an explicit expiry so trusted devices (family home laptop) don't require the
// password every visit. In addition, the server keeps a per-family list of
// remembered browsers (with a stable device_id + friendly label) so users can
// see and revoke every trusted device from their portal.

const KEY = "bailey_family_remembered";
const DEVICE_ID_KEY = "bailey_family_device_id";
const DAYS = 90;

function _uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Stable per-browser ID that persists across visits until localStorage is
// cleared. Used to correlate the device with a server-side trusted-devices
// entry so revocation from the portal can invalidate this exact browser.
export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = _uuid();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return _uuid();
  }
}

// Human-friendly label built from the User-Agent. Not sensitive; just a hint
// like "Chrome on macOS" so the user can identify each session in the list.
export function getDeviceLabel() {
  const ua = navigator.userAgent || "";
  let browser = "Browser";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR|Opera/i.test(ua)) browser = "Opera";
  else if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) browser = "Chrome";
  else if (/Firefox\//i.test(ua)) browser = "Firefox";
  else if (/Safari\//i.test(ua)) browser = "Safari";

  let os = "device";
  if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Mac OS X|Macintosh/i.test(ua)) os = "macOS";
  else if (/Windows/i.test(ua)) os = "Windows";
  else if (/Linux/i.test(ua)) os = "Linux";

  return `${browser} on ${os}`;
}

export function rememberFamily(magicToken) {
  try {
    const payload = {
      token: magicToken,
      trusted_at: Date.now(),
      expires_at: Date.now() + DAYS * 24 * 60 * 60 * 1000,
    };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be blocked (private mode); silently no-op.
  }
}

export function forgetFamily() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

function _readValid() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p?.token || !p?.expires_at) return null;
    if (Date.now() > p.expires_at) {
      localStorage.removeItem(KEY);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

export function getRememberedFamily() {
  return _readValid()?.token || null;
}

export function getTrustedSince() {
  return _readValid()?.trusted_at || null;
}

export const REMEMBER_DAYS = DAYS;
